import React, { useState } from "react";
import { ThemeProvider } from "../components/ThemeProvider";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";

const commonFeatures = [
  { included: true, text: "Access to Pi - Predictive intelligence" },
  { included: true, text: "Access to all scanners" },
  { included: true, text: 'Access to "Automate Via Algotest"' },
  { included: true, text: 'Access to "Updated Learning Content"' },
];

const pricingPlans = [
  {
    title: "Monthly",
    price: "₹1,499",
    period: "/month",
    billingCycle: "monthly",
    note: "",
    popular: false,
  },
  {
    title: "Yearly",
    price: "₹6,999",
    period: "/year",
    billingCycle: "yearly",
    note: "Save 61% (Only ₹584/month)",
    popular: true,
  },
];

const getCardClasses = (isMarketing, isActive) => {
  const base = isMarketing
    ? "group relative p-8 flex flex-col h-full rounded-2xl backdrop-blur-md bg-black/60 border shadow-xl"
    : "group relative p-8 flex flex-col h-full rounded-xl bg-darkMode-layer1 border shadow-sm";
  const hover =
    "transition-all duration-300 hover:shadow-md hover:scale-[1.02]";
  const border = isMarketing
    ? isActive
      ? "border-market-teal/30 hover:border-market-teal/50"
      : "border-market-teal/10"
    : isActive
    ? "border-darkMode-green"
    : "border-darkMode-layer2";

  return `${base} ${hover} ${border}`;
};

const SubscriptionsContent = ({ themeVariant }) => {
  const [billingCycle, setBillingCycle] = useState("yearly");
  const isMarketing = themeVariant === "marketing";

  const headingClasses = isMarketing
    ? "text-3xl md:text-4xl font-bold text-white mb-4"
    : "text-2xl font-semibold text-darkMode-text mb-4";

  const subheadingClasses = isMarketing
    ? "text-white/80 max-w-3xl mx-auto mb-12"
    : "text-darkMode-subText max-w-3xl mx-auto mb-12";

  const toggleClasses = isMarketing
    ? "bg-black/60 border-2 border-market-teal/30 p-1 rounded-full"
    : "bg-darkMode-layer1 p-1 rounded-full";

  const toggleButtonActiveClasses = isMarketing
    ? "bg-market-teal text-black"
    : "bg-darkMode-green text-darkMode-background";

  const toggleButtonInactiveClasses = isMarketing
    ? "text-white hover:bg-white/10"
    : "text-darkMode-text hover:bg-darkMode-layer2";

  return (
    <div className={`${isMarketing ? "" : "bg-darkMode-background"} py-20`}>
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h1 className={headingClasses}>
            {isMarketing ? (
              <>
                <span className="text-market-teal">Pricing </span>Plans
              </>
            ) : (
              "Pricing Plans"
            )}
          </h1>
          <p className={subheadingClasses}>
            Choose the plan that best fits your trading needs and goals. All
            plans include access to our advanced market analytics and real-time
            data.
          </p>

          {/* Billing toggle */}
          <div className="flex justify-center mb-12">
            <div className={`flex items-center ${toggleClasses}`}>
              {["monthly", "yearly"].map((cycle) => (
                <button
                  key={cycle}
                  onClick={() => setBillingCycle(cycle)}
                  aria-pressed={billingCycle === cycle}
                  className={`px-6 py-2 rounded-full transition-all duration-300 ease-in-out ${
                    billingCycle === cycle
                      ? toggleButtonActiveClasses
                      : toggleButtonInactiveClasses
                  }`}
                >
                  {cycle === "monthly" ? (
                    "Monthly"
                  ) : (
                    <>
                      Yearly{" "}
                      <span className="text-xs ml-1 opacity-80">Save 61%</span>
                    </>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
          {pricingPlans.map((plan) => {
            const isActive = billingCycle === plan.billingCycle;

            return (
              <div
                key={plan.title}
                className={getCardClasses(isMarketing, isActive)}
              >
                {isMarketing && (
                  <div className="absolute inset-0 rounded-2xl ring-2 ring-market-teal/0 group-hover:ring-market-teal/30 transition-all duration-300" />
                )}

                {plan.popular && (
                  <div
                    className={
                      isMarketing
                        ? "absolute -top-4 left-1/2 transform -translate-x-1/2 bg-market-teal text-black text-xs font-bold py-1 px-3 rounded-full shadow-lg group-hover:scale-110 transition-transform duration-300"
                        : "absolute -top-4 left-1/2 transform -translate-x-1/2 bg-darkMode-green text-darkMode-background text-xs font-bold py-1 px-3 rounded-full shadow-lg"
                    }
                  >
                    Most Popular
                  </div>
                )}

                <h3
                  className={
                    isMarketing
                      ? "text-2xl font-bold mb-2 text-white group-hover:text-market-teal transition-colors duration-300"
                      : "text-xl font-semibold text-darkMode-text mb-2"
                  }
                >
                  {plan.title}
                </h3>
                <div className="mb-2">
                  <span
                    className={
                      isMarketing
                        ? "text-4xl font-bold text-white group-hover:text-market-teal transition-colors duration-300"
                        : "text-3xl font-bold text-darkMode-text"
                    }
                  >
                    {plan.price}
                  </span>
                  <span
                    className={
                      isMarketing
                        ? "text-white/60 ml-1"
                        : "text-darkMode-subText ml-1"
                    }
                  >
                    {plan.period}
                  </span>
                </div>

                {plan.note && (
                  <div
                    className={
                      isMarketing
                        ? "mb-4 text-market-teal font-medium text-sm"
                        : "mb-4 text-darkMode-green font-medium text-sm"
                    }
                  >
                    {plan.note}
                  </div>
                )}

                <p
                  className={
                    isMarketing
                      ? "text-white/70 mb-6 group-hover:text-white/90 transition-colors duration-300"
                      : "text-darkMode-subText mb-6"
                  }
                >
                  Full access to all features with {plan.billingCycle} billing
                </p>

                <div className="flex-grow">
                  <ul className="space-y-3 mb-8">
                    {commonFeatures.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check
                          className={
                            isMarketing
                              ? "text-market-teal h-5 w-5 mr-2 mt-0.5 flex-shrink-0 group-hover:scale-110 transition-transform duration-300"
                              : "text-darkMode-green h-5 w-5 mr-2 mt-0.5 flex-shrink-0"
                          }
                        />
                        <span
                          className={
                            isMarketing
                              ? "text-white/80 group-hover:text-white transition-colors duration-300"
                              : "text-darkMode-text"
                          }
                        >
                          {feature.text}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>

                <Link to="/signup" className="z-40">
                  <button
                    className={
                      isMarketing
                        ? `w-full rounded-lg py-3 text-center font-medium transition-all duration-300 ease-in-out ${
                            isActive
                              ? "bg-market-teal text-black"
                              : " bg-white/10 text-white hover:text-black"
                          }  hover:bg-market-teal/90 hover:scale-105`
                        : "w-full rounded-lg py-3 text-center font-medium transition-all duration-300 ease-in-out bg-darkMode-green text-darkMode-background hover:bg-darkMode-green/90 hover:scale-105"
                    }
                  >
                    Get Started
                  </button>
                </Link>
              </div>
            );
          })}
        </div>

        {/* FAQ Section */}
        <div
          className={`mt-12 sm:mt-20 max-w-3xl mx-auto rounded-xl ${
            isMarketing
              ? "bg-black/50 border border-white/10 text-white"
              : "bg-darkMode-layer1 border border-darkMode-layer2 text-darkMode-text"
          } p-4 sm:p-8 shadow-sm transition-all duration-300 hover:shadow-md`}
        >
          <h2 className="text-xl sm:text-2xl font-bold mb-4 text-center">
            Frequently Asked Questions
          </h2>
          <div className="space-y-4 sm:space-y-6">
            {[
              {
                q: "Do you offer refunds?",
                a: (
                  <>
                    No, we do not offer any refunds, kindly read{" "}
                    <a
                      href="/refund"
                      className={
                        isMarketing
                          ? "text-market-teal hover:underline"
                          : "text-darkMode-green hover:underline"
                      }
                    >
                      Refund Policy
                    </a>{" "}
                    carefully.
                  </>
                ),
              },
              {
                q: "Is my payment secure?",
                a: "Yes, your payment is always secured by Razorpay.",
              },
              {
                q: "Can I have EMI plans?",
                a: "Yes, you can choose various EMI plans that are supported by Razorpay & your bank, at checkout page.",
              },
              {
                q: "What payment methods do you accept?",
                a: "We accept all major credit cards, debit cards, net banking and UPI transactions.",
              },
            ].map(({ q, a }, i) => (
              <div key={i}>
                <h3
                  className={
                    isMarketing
                      ? "text-market-teal font-semibold mb-2 text-sm sm:text-base"
                      : "text-darkMode-green font-semibold mb-2 text-sm sm:text-base"
                  }
                >
                  {q}
                </h3>
                <p
                  className={
                    isMarketing
                      ? "text-white/70 text-sm sm:text-base"
                      : "text-darkMode-subText text-sm sm:text-base"
                  }
                >
                  {a}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export const MarketingSubscriptions = () => (
  <ThemeProvider variant="marketing">
    <SubscriptionsContent themeVariant="marketing" />
  </ThemeProvider>
);

export const DashboardSubscriptions = () => (
  <ThemeProvider variant="dashboard">
    <SubscriptionsContent themeVariant="dashboard" />
  </ThemeProvider>
);

const Subscriptions = ({ variant = "marketing" }) => {
  return variant === "marketing" ? (
    <MarketingSubscriptions />
  ) : (
    <DashboardSubscriptions />
  );
};

export default Subscriptions;
